'use client';

import React, { useCallback, useState } from 'react';
import Image from '@/components/atoms/Image';
import { PhotoIcon, QueueListIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import Link from '@/components/atoms/link';
import Button from '@/components/atoms/button';
import Badge from '@/components/atoms/badge';
import useAuthDrawer from '@/components/organisms/authentication/auth-drawer-provider/hooks/useAuthDrawer';
import { SIGN_IN_DRAWER_ID } from '@/components/organisms/authentication/sign-in-drawer';
import { useTranslations } from 'use-intl';
import useFormat from '@/hooks/useFormat';
import { classnames } from '@/utils/classnames/classnames';
import { resolveAddToCartAvailability } from '@/utils/product/add-to-cart-availability';
import { resolveStockStatus } from '@/utils/product/resolve-stock-status';
import { resolveTierUnitPrice } from '@/utils/product/resolve-tier-unit-price';
import QuantityWidget from '@/components/atoms/quantity-widget';
import { ProductTileV2Props, ProductTileV2Variant } from './types';

// Fixed dimensions for the image area — kept consistent regardless of
// whether the source URL loads, so the grid never reflows when an
// image fails to fetch from the CDN. Backend will eventually serve
// higher-resolution images via a new CDN; until then a clean blank
// placeholder is shown for any URL that 4xx/5xxs.
//
// `variant`: 'grid' matches Dan's v0 panel — min-height 200 with the image
// box capped at 180px tall (full width, centered); 'list' is square at
// 140×140 so the row stays compact at typical viewport widths.
const ImageArea = ({ src, alt, variant }: { src?: string; alt: string; variant: ProductTileV2Variant }) => {
  const [hasError, setHasError] = useState(false);

  // Reset the error flag when the source URL changes — otherwise the
  // placeholder would stick after a re-render with a fresh URL.
  // EWM-760 — adjusted during render instead of a cascading `useEffect`;
  // same trigger (`src` identity change), one render sooner.
  const [prevSrc, setPrevSrc] = useState(src);
  if (prevSrc !== src) {
    setPrevSrc(src);
    setHasError(false);
  }

  const showPlaceholder = !src || hasError;

  if (variant === 'list') {
    return (
      <div className="relative flex size-[140px] shrink-0 items-center justify-center bg-slate-50 p-3">
        {showPlaceholder ? (
          <div className="flex size-full items-center justify-center bg-gray-100 text-gray-400" aria-hidden>
            <PhotoIcon className="size-8" />
          </div>
        ) : (
          <Image
            src={src}
            alt={alt}
            fill
            loading="lazy"
            sizes="140px"
            className="object-contain mix-blend-multiply"
            onError={() => setHasError(true)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[200px] w-full items-center justify-center bg-slate-50 p-4">
      {showPlaceholder ? (
        <div
          className="flex h-[180px] w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-400"
          aria-hidden
        >
          <PhotoIcon className="size-10" />
        </div>
      ) : (
        <div className="relative mx-auto h-[180px] w-full">
          <Image
            src={src}
            alt={alt}
            fill
            loading="lazy"
            sizes="(max-width: 744px) 50vw, 200px"
            className="object-contain mix-blend-multiply"
            onError={() => setHasError(true)}
          />
        </div>
      )}
    </div>
  );
};

// EWM-187 product tile, v2.
//
// Two layouts share the same data + actions:
//   - 'grid' (default): vertical card. Image top, content stacked below.
//   - 'list': horizontal row. Image left, content middle, price + CTA
//             right-aligned. Designed for browsing a long catalogue
//             where users scan model + spec + price quickly.
//
// Pure presentational: no data fetching, no context, no router —
// every behaviour is via callback props so this can be storied in any
// role / state / variant combo without external setup.
const ProductTileV2 = ({
  item: {
    name,
    sku,
    price,
    discountedPrice,
    rrp,
    currency,
    images,
    url,
    inStock,
    isRemovedFromSale,
    isStockDueSoonEligible,
  },
  brand,
  badge,
  role = 'master',
  variant = 'grid',
  priceUnavailable = false,
  stockUnavailable = false,
  backOrdersAllowed = false,
  showActions = true,
  showRrp = true,
  showQuantitySelector = false,
  hoverScale = false,
  canAddToPurchaseList,
  priceBreaks,
  onAddToCart,
  onAddToPurchaseList,
  onTileClick,
  className = '',
}: ProductTileV2Props) => {
  const [addingToCart, setAddingToCart] = useState(false);
  // EWM-926 — selected order quantity. Defaults to 1 on every mount, so a fresh
  // PLP load (or a filter/sort/page change that remounts the grid) always starts
  // at 1 per AC. Deliberately NOT reset after a successful add: the PDP leaves
  // its stepper where the customer put it, and this tile matches that pattern.
  const [quantity, setQuantity] = useState(1);
  const translate = useTranslations();
  const { mode: authMode, open: openAuthDrawer } = useAuthDrawer();

  const handleAddToCart = useCallback(async () => {
    setAddingToCart(true);
    try {
      await onAddToCart?.(quantity);
    } finally {
      setAddingToCart(false);
    }
  }, [onAddToCart, quantity]);

  const { formatCurrency } = useFormat();

  // EWM-926 / EWM-523 — the unit price for the quantity currently selected. The
  // tiers come off the CT snapshot and are resolved by the SAME helper the cart
  // uses, so a tier crossing shows the identical price the cart will charge.
  // Passing `undefined` as the base makes the resolver return a Money ONLY when
  // a tier actually qualifies; otherwise we keep the tile's own dollar `price`.
  // Tier pricing applies to the BASE price, mirroring the cart (map-lineitem):
  // a product discount is orthogonal and still wins the display below.
  const tierUnitPrice = resolveTierUnitPrice(undefined, priceBreaks, quantity);
  const basePrice =
    tierUnitPrice?.centAmount != null
      ? tierUnitPrice.centAmount / Math.pow(10, tierUnitPrice.fractionDigits ?? 2)
      : price;

  const isDiscounted = typeof discountedPrice === 'number' && discountedPrice < basePrice;
  const displayedPrice = isDiscounted ? discountedPrice : basePrice;
  // EWM-525 — the RRP reference (the authoritative `price-rrp` value merged from
  // the CT snapshot, same source as the PDP pill) shown in its OWN line, distinct
  // from a product-discount "was" price. Higher-only: only when RRP exceeds the
  // dealer's shown price — so guests, whose displayed price already IS the RRP,
  // never see a redundant RRP line.
  const rrpReference = showRrp && typeof rrp === 'number' && rrp > displayedPrice ? rrp : undefined;

  // Stock gating mirrors PDP cart-cta — the button stays visible but is
  // disabled and re-labelled when the product is out of stock for the
  // dealer's selected supply channel. `inStock` comes from CT-enriched
  // data (`variant.availability.channels[supplyChannelId]?.isOnStock`),
  // so it's per-store / per-supply-channel.
  //
  // `priceUnavailable` and `stockUnavailable` are the CT-enrichment
  // failure gates: when CT didn't return a price (resp. a stock signal),
  // the tile renders "Unavailable" / "Out of stock" and disables the
  // cart CTA — we never let a user buy at an unknown price OR an
  // unknown stock state. `priceUnavailable` wins over `stockUnavailable`
  // in label precedence because not knowing the price is a more serious
  // problem than not knowing stock.
  // EWM-545 — the stock LABEL distinguishes a new/pending product awaiting
  // first stock ("Stock due soon") from a genuine "Out of stock". This is
  // label-only: the cart CTA below still gates on `inStock`/`stockUnavailable`
  // via `resolveAddToCartAvailability`, so a due-soon product stays
  // non-purchasable — only the label differs.
  const stockStatus = resolveStockStatus({ inStock, stockUnavailable, isStockDueSoonEligible });
  const isGuest = role === 'guest';
  const isViewOnly = role === 'view-only';
  // Master only — guests get a dedicated Sign-in CTA in place of the
  // cart button; view-only loses both surfaces entirely.
  const showAddToCart = showActions && !isViewOnly && !isGuest;
  const showSignInCta = showActions && isGuest;
  // EWM-653 — purchase-list access is now a centralized capability
  // (useShoppingListAccess, Master-only). Fall back to the role check when the
  // prop isn't supplied so legacy consumers / stories keep current behaviour.
  const showAddToPurchaseList = showActions && (canAddToPurchaseList ?? role === 'master');
  // EWM-390: only show the brand line when CT actually supplies a brand.
  // The old `brand ?? name.split(' ')[0]` fallback printed the first word of
  // the product name as a fake brand for brand-less products. With no brand we
  // render nothing; the `gap` between Identifier children collapses when the
  // brand <p> is omitted, so the name moves up (placement adjusts per AC).
  const displayBrand = brand?.trim() ? brand.trim().toUpperCase() : undefined;
  // EWM-360 — Add to Cart enabled-state + label resolved by the shared helper.
  // Out-of-stock products become a "Back Order" CTA (still enabled) when the
  // customer's BU allows back-orders; GO ("removed from sale") products stay
  // disabled regardless. `priceUnavailable` still wins (disabled + Unavailable).
  const { disabled: cartDisabled, labelState: cartLabelState } = resolveAddToCartAvailability({
    inStock,
    stockUnavailable,
    priceUnavailable,
    removedFromSale: isRemovedFromSale,
    backOrdersAllowed,
  });

  // Shared sub-renders — keep the layout-switching tile body small.
  const Identifier = (
    <>
      {displayBrand && <p className="text-12 tracking-wide text-gray-500 uppercase">{displayBrand}</p>}
      <Link href={url ?? '#'} className="block" underlineOnHover={false} onClick={onTileClick}>
        <h3 className="text-15 hover:text-primary line-clamp-3 leading-snug font-bold text-gray-800">{name}</h3>
      </Link>
      {sku && (
        <p className="text-12 font-medium text-gray-400">
          {translate('common.cat-no')}: {sku}
        </p>
      )}
    </>
  );

  // Boolean in/out indicator. Per current UX spec we don't surface
  // quantities (competitive intelligence — dealers shouldn't see each
  // other's stock levels publicly), only the binary in/out state.
  const StockIndicator = (
    <p className={classnames('text-12 font-semibold', stockStatus === 'in-stock' ? 'text-green-700' : 'text-red-600')}>
      {stockStatus === 'in-stock'
        ? translate('product.in-stock')
        : stockStatus === 'due-soon'
          ? translate('product.stock-due-soon')
          : translate('product.out-of-stock')}
    </p>
  );

  const PriceBlock = (
    <div className="mt-1 flex flex-col gap-0.5">
      {priceUnavailable ? (
        <p className="text-15 font-semibold text-gray-500">{translate('product.price-unavailable')}</p>
      ) : (
        <>
          <p className="text-22 flex flex-wrap items-baseline gap-2 font-bold text-gray-900">
            <span>{formatCurrency(displayedPrice, currency)}</span>
            {isDiscounted && (
              // Product-discount "was" price — struck through, NOT the RRP. The
              // sr-only label stops assistive tech reading it as the price to pay.
              <s className="text-13 font-normal text-gray-400">
                <span className="sr-only">{translate('pdp.was-price')} </span>
                {formatCurrency(basePrice, currency)}
              </s>
            )}
          </p>
          {rrpReference !== undefined && (
            <p className="text-13 text-gray-500">
              {translate('pdp.rrp')} {formatCurrency(rrpReference, currency)}
            </p>
          )}
        </>
      )}
      {StockIndicator}
    </div>
  );

  const cartLabel =
    cartLabelState === 'price-unavailable'
      ? translate('product.price-unavailable')
      : cartLabelState === 'back-order'
        ? translate('pdp.back-order')
        : cartLabelState === 'out-of-stock'
          ? translate('pdp.out-of-stock-action')
          : translate('cart.add');

  // EWM-440 — only the purchasable state gets the cart icon + the responsive
  // "Add" / "Add to cart" relabel; the other states keep their single label.
  const isAddState = cartLabelState === 'available';

  // EWM-926 — with the quantity selector on, the tile switches to a two-row
  // action area: [quantity | Add to Purchase List] above, full-width Add to Cart
  // below. `showAddToCart` is the gate, so guests (who get the sign-in CTA) and
  // view-only users never reach it — satisfying the AC that they see none of
  // these controls. Carousels leave `showQuantitySelector` off and keep the
  // original single row untouched.
  const showQuantity = showQuantitySelector && showAddToCart;

  const AddToCartButton = (
    // Dan v0's card CTA is a bespoke 41px button (py-2.5, line-height
    // 21, semibold, 4px radius) — taller/heavier than the shared
    // Button atom's size="s" (32px). Override per-instance to match the
    // mock exactly without changing the global atom (EWM-317).
    <Button
      variant="primary"
      size="s"
      loading={addingToCart}
      onClick={handleAddToCart}
      // `flex-1` grows along the row in the single-row layout; in the stacked
      // layout the parent is a COLUMN, where `flex-1` would stretch the button
      // vertically instead of filling the width — so use `w-full` there.
      className={classnames('!rounded !px-4 !py-2.5 !leading-loose !font-semibold', showQuantity ? 'w-full' : 'flex-1')}
      disabled={cartDisabled}
      // Keep the full action as the accessible name even when the visible
      // label shortens to "Add" on mobile/tablet.
      aria-label={isAddState ? translate('cart.add') : undefined}
    >
      {isAddState ? (
        <span className="flex items-center justify-center gap-2 whitespace-nowrap">
          <ShoppingCartIcon className="size-5" aria-hidden />
          <span className="lg:hidden">{translate('pdp.add')}</span>
          <span className="hidden lg:inline">{translate('cart.add')}</span>
        </span>
      ) : (
        cartLabel
      )}
    </Button>
  );

  const SignInCta = (
    // Sign-in is the app-wide AuthDrawer (see AuthDrawerProvider), not a
    // `/login` route — that path isn't a published page and 404s
    // (EWM-385). Mirror the header's sign-in triggers and open the drawer.
    <button
      type="button"
      onClick={() => openAuthDrawer('sign-in')}
      aria-haspopup="dialog"
      aria-controls={SIGN_IN_DRAWER_ID}
      aria-expanded={authMode === 'sign-in'}
      className="bg-primary hover:bg-primary/90 text-14 flex-1 rounded px-4 py-2.5 text-center font-semibold whitespace-nowrap text-white transition-colors"
    >
      {translate('product.sign-in-to-buy')}
    </button>
  );

  const AddToPurchaseListButton = (
    <button
      type="button"
      aria-label={translate('product.add-to-list')}
      onClick={onAddToPurchaseList}
      className="hover:border-primary hover:text-primary flex items-center justify-center rounded border border-gray-300 p-2.5 text-gray-600 transition-colors"
    >
      {/* Decorative — the button itself carries the accessible name. */}
      <QueueListIcon className="size-5" aria-hidden />
    </button>
  );

  const QuantitySelector = (
    // EWM-278 / EWM-641 — matching the PDP deliberately: `minValue={1}` and NO
    // `maxValue`, so only the widget's shared MAX_QUANTITY ceiling applies. We do
    // not clamp to available stock — that would let anyone read a SKU's warehouse
    // level straight off the stepper, on 20 tiles at once. Over-ordering is
    // caught at cart/checkout, exactly as it is from the PDP.
    <QuantityWidget
      value={quantity}
      onChange={setQuantity}
      minValue={1}
      variant="tile"
      showLabel={false}
      spinbutton
      disabled={cartDisabled}
    />
  );

  const Actions = showQuantity ? (
    <div className="flex flex-col gap-2">
      {/* Row 1 — quantity fills the remainder; Purchase List keeps its size.
          `flex-wrap` + the stepper's `min-w-20` is a safety valve for the very
          narrowest tiles (≤~375px in the 2-col mobile grid): rather than crush
          the quantity input, the Purchase List button drops to its own line.
          On mainstream widths both still share one row, per AC. */}
      <div className="flex flex-wrap items-stretch gap-2">
        <div className="min-w-20 flex-1">{QuantitySelector}</div>
        {showAddToPurchaseList && AddToPurchaseListButton}
      </div>
      {/* Row 2 — Add to Cart, full width. */}
      {AddToCartButton}
    </div>
  ) : (
    <div className="flex items-center gap-2">
      {showAddToCart && AddToCartButton}
      {showSignInCta && SignInCta}
      {showAddToPurchaseList && AddToPurchaseListButton}
    </div>
  );

  if (variant === 'list') {
    return (
      <div
        className={classnames(
          'relative flex border border-gray-200 bg-white',
          'flex-col sm:flex-row sm:items-stretch',
          className,
        )}
      >
        {badge && (
          <Badge variant={badge} className="absolute top-0 left-0 z-10">
            {badge.replace(/-/g, ' ')}
          </Badge>
        )}

        <Link href={url ?? '#'} className="block shrink-0" underlineOnHover={false} onClick={onTileClick}>
          <ImageArea src={images?.[0]} alt={name} variant="list" />
        </Link>

        <div className="flex flex-1 flex-col gap-2 p-4 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex flex-1 flex-col gap-1">{Identifier}</div>

          <div className="flex flex-col gap-3 sm:items-end sm:text-right">
            {PriceBlock}
            <div className="w-full sm:w-56">{Actions}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        'relative flex h-full flex-col border border-gray-200 bg-white',
        // EWM-1103 — EWM-187's subtle hover scale-up, grid only. `hover:z-10`
        // lifts the scaled card over its neighbours (1.02 on a ~220px tile is
        // ~2px a side, well inside the grid's 16px gap); reduced motion gets
        // neither the transition nor the scale.
        {
          'transition-transform duration-200 ease-out hover:z-10 hover:scale-[1.02] motion-reduce:transition-none motion-reduce:hover:scale-100':
            hoverScale,
        },
        className,
      )}
    >
      {badge && (
        <Badge variant={badge} className="absolute top-0 left-0 z-10">
          {badge.replace(/-/g, ' ')}
        </Badge>
      )}

      {/* EWM-375 — pass the `block` prop (not just `className="block"`) so the
          Link atom omits its default `w-fit`. Around the grid ImageArea (a
          `fill` image with no intrinsic width) `w-fit` collapsed the link to
          ~0px, so grid images rendered at width 0 / invisible. The list
          variant survived only because its image sits in a fixed 140px box. */}
      <Link href={url ?? '#'} block className="block" underlineOnHover={false} onClick={onTileClick}>
        <ImageArea src={images?.[0]} alt={name} variant="grid" />
      </Link>

      <div className="flex flex-1 flex-col gap-2 p-4 pt-3">
        {Identifier}
        <div className="flex-1" />
        {PriceBlock}
        <div className="mt-2">{Actions}</div>
      </div>
    </div>
  );
};

export default ProductTileV2;
